<script setup lang="ts">
import { useQueryClient } from "@tanstack/vue-query";
import { ModK } from "ls/common/keyboardShortcuts";
import { useKeyboardShortcut } from "ls/common/useKeyboardShortcut";
import { CommandDialog, CommandInput, CommandList } from "ls/components/command";
import { ArrowUpDownIcon, CornerDownLeftIcon } from "lucide-vue-next";
import { ref, shallowRef, watch } from "vue";
import { queries as recentsQueries } from "../recents/queries";
import CommandPageRecents from "./CommandPageRecents.vue";
import CommandPageSearch from "./CommandPageSearch.vue";
import { queries } from "./queries";
import { useCommandKControls } from "./useCommandKControls";

const { isOpen } = useCommandKControls();

// remember the active element before opening the dialog
// so that it can be focused again when the dialog is closed
const savedActiveElement = shallowRef<Element>();
const savedSelectionRange = ref<Range>();

function saveFocus() {
  savedActiveElement.value = document.activeElement ?? undefined;
  const selection = window.getSelection();
  if (selection && selection.rangeCount > 0) {
    savedSelectionRange.value = selection.getRangeAt(0);
  }
}

// Function to restore the saved selection
function restoreFocus() {
  if (savedActiveElement.value instanceof HTMLElement) {
    savedActiveElement.value.focus();
  }
  if (savedSelectionRange.value) {
    const selection = window.getSelection();
    if (selection) {
      selection.removeAllRanges();
      selection.addRange(savedSelectionRange.value);
    }
  }
}

useKeyboardShortcut(ModK, event => {
  saveFocus();
  event.preventDefault();
  isOpen.value = true;
});

const searchTerm = ref("");
const queryClient = useQueryClient();
void queryClient.prefetchQuery(queries.search());
void queryClient.prefetchQuery(recentsQueries.recents());

watch(isOpen, value => {
  const isClosed = !value;
  if (isClosed) {
    searchTerm.value = "";
    restoreFocus();
  }
}, {
  // use flush 'post' to restore original focus only after the dialog is closed and removed from DOM
  // otherwise the focus will remain within Dialog focus trap
  flush: "post",
});
</script>

<template>
  <CommandDialog
    v-if="isOpen"
    v-model:open="isOpen"
    title="Search"
    description="Search workspaces, folders and files across all your organizations"
  >
    <CommandInput v-model="searchTerm" placeholder="Search" @close="isOpen = false" />
    <CommandList @escape-key-down="isOpen = false">
      <CommandPageSearch
        v-if="searchTerm"
        @select="isOpen = false"
      />
      <CommandPageRecents
        v-if="!searchTerm"
        @select="isOpen = false"
      />
    </CommandList>
    <div class="flex gap-4 border-t border-slate-300 px-3 py-1 text-xs/6 text-slate-500">
      <div class="flex items-center gap-1">
        Select
        <kbd>
          <ArrowUpDownIcon class="size-3" aria-hidden="true" />
          <span class="sr-only">Arrow Up Arrow Down</span>
        </kbd>
      </div>
      <div class="flex items-center gap-1">
        Open
        <kbd>
          <CornerDownLeftIcon class="size-3" aria-hidden="true" />
          <span class="sr-only">Enter</span>
        </kbd>
      </div>
      <div class="ml-auto flex items-center gap-1">
        Search <kbd>{{ ModK.label }}</kbd>
      </div>
    </div>
  </CommandDialog>
</template>
